import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './Table.css';

const __tableStyleTagId = 'table-widget-dynamic-styles';

const __tableEnsureStyleTag = () => {
  if (typeof document === 'undefined') return null;
  let tag = document.getElementById(__tableStyleTagId);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = __tableStyleTagId;
    document.head.appendChild(tag);
  }
  return tag;
};

const __tableHash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
};

const __tableEnsureRule = (className, cssText) => {
  const tag = __tableEnsureStyleTag();
  if (!tag) return;
  if (tag.textContent.includes(`.${className}{`)) return;
  tag.appendChild(document.createTextNode(`\n.${className}{${cssText}}\n`));
};

const __tableToCssSize = (v) => (typeof v === 'number' ? `${v}px` : (v ?? ''));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const __tableGetDynClass = ({
  width,
  height,
  headerBgColor,
  headerTextColor,
  bodyBgColor,
  bodyTextColor
}) => {
  const payload = JSON.stringify({ width, height, headerBgColor, headerTextColor, bodyBgColor, bodyTextColor });
  const cls = `table-dyn-${__tableHash(payload)}`;

  const w = __tableToCssSize(width) || '100%';
  const h = __tableToCssSize(height) || '100%';

  __tableEnsureRule(
    cls,
    `width:${w};height:${h};` +
      `--table-header-bg:${headerBgColor};` +
      `--table-header-text:${headerTextColor};` +
      `--table-body-bg:${bodyBgColor};` +
      `--table-body-text:${bodyTextColor};`
  );

  return cls;
};

const TableWidget = ({
  title,
  xData = [],
  curves = [],
  draggable = false,
  transparentBackground = false,
  dims = { width: '100%', height: '100%' },
  headerBgColor = 'var(--header-background)',
  headerTextColor = 'var(--header-color)',
  bodyBgColor = 'var(--cell-background)',
  bodyTextColor = 'var(--cell-color)'
}) => {
  const widgetRef = useRef(null);
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [widgetSize, setWidgetSize] = useState({ width: 360, height: 320 });

  useEffect(() => {
    if (!widgetRef.current || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setWidgetSize({ width, height });
    });

    observer.observe(widgetRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setCols([
      {
        field: 't',
        headerName: 'Time',
        flex: 1,
        minWidth: 170
      },
      ...curves.map((c) => ({
        field: `${c.pin}_${c.deviceId}`,
        headerName: c.yLabel || c.pin,
        flex: c.flex || 1,
        minWidth: 100
      }))
    ]);

    const newRows = xData.reduce((accumulator, utcString, idx) => {
      const shouldSkip = curves.some((c) => {
        if (!c.customize) return false;
        const rawValue = Array.isArray(c.yData) ? c.yData[idx] : null;
        if (rawValue === null || isNaN(rawValue)) return true;
        return rawValue < c.min || rawValue > c.max;
      });
      if (shouldSkip) {
        return accumulator;
      }

      const localTimeString = new Date(utcString).toLocaleString('en-US', {
        timeZone: 'Asia/Damascus',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const rowObj = { id: idx, t: localTimeString };

      curves.forEach((c) => {
        const rawVal = Array.isArray(c.yData) ? c.yData[idx] : null;
        const fieldKey = `${c.pin}_${c.deviceId}`;
        rowObj[fieldKey] = rawVal === undefined || rawVal === null ? null : rawVal;
      });

      accumulator.push(rowObj);
      return accumulator;
    }, []);

    setRows(newRows);
  }, [xData, curves]);

  const tableMetrics = useMemo(() => {
    const short = widgetSize.height < 260;
    const narrow = widgetSize.width < 330;
    const density = short || narrow ? 0.82 : 1;

    return {
      columnHeaderHeight: Math.round(clamp(widgetSize.height * 0.17 * density, 40, 70)),
      rowHeight: Math.round(clamp(widgetSize.height * 0.16 * density, 38, 66)),
      titleSuffix: curves.length > 0 ? ` (${curves.map((c) => c.pin).join(',')})` : '',
    };
  }, [curves, widgetSize.height, widgetSize.width]);

  const pageSizeOptions = useMemo(() => {
    const total = Math.max(rows.length, 10);
    return [5, 10, 20].filter((value) => value <= total || value === 10);
  }, [rows.length]);

  const totalPages = Math.max(1, Math.ceil(rows.length / paginationModel.pageSize));

  useEffect(() => {
    setPaginationModel((prev) => {
      const nextPage = Math.min(prev.page, Math.max(0, totalPages - 1));
      return nextPage === prev.page ? prev : { ...prev, page: nextPage };
    });
  }, [totalPages]);

  const handlePageSizeChange = (event) => {
    const nextPageSize = Number(event.target.value);
    setPaginationModel({ page: 0, pageSize: nextPageSize });
  };

  const goToPage = (page) => {
    setPaginationModel((prev) => ({
      ...prev,
      page: clamp(page, 0, totalPages - 1),
    }));
  };

  const visiblePageNumbers = useMemo(() => {
    const pages = [];
    const current = paginationModel.page;
    const start = clamp(current - 2, 0, Math.max(0, totalPages - 5));
    const end = Math.min(totalPages, start + 5);
    for (let page = start; page < end; page += 1) pages.push(page);
    return pages;
  }, [paginationModel.page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return rows.slice(start, start + paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, rows]);

  const visibleRows = useMemo(() => {
    const minimumRows = widgetSize.height >= 340 ? 2 : 1;
    const rowsForDisplay = paginatedRows.slice(0, Math.max(minimumRows, Math.min(paginationModel.pageSize, 4)));
    if (rowsForDisplay.length >= minimumRows) return rowsForDisplay;

    return [
      ...rowsForDisplay,
      ...Array.from({ length: minimumRows - rowsForDisplay.length }, (_, idx) => ({
        id: `empty-${idx}`,
        __empty: true,
      })),
    ];
  }, [paginatedRows, paginationModel.pageSize, widgetSize.height]);

  const handleDragStart = (e) => {
    const widgetEl = e.target.closest('.widget');
    if (!widgetEl) return;
    const widgetId = widgetEl.id;
    const rect = widgetEl.getBoundingClientRect();
    const offsetX = e.clientX - rect.x;
    const offsetY = e.clientY - rect.y;
    let dragData = `table;${widgetId};${offsetX};${offsetY};`;
    dragData += widgetId.startsWith('base') ? 'add' : 'move';
    e.dataTransfer.setData('text/plain', dragData);
  };

  const setGrabbing = (e, on) => {
    if (!draggable) return;
    const el = e.target.closest('.widget-container');
    if (!el) return;
    el.classList.toggle('is-grabbing', Boolean(on));
  };

  const dynClass = __tableGetDynClass({
    width: dims?.width,
    height: dims?.height,
    headerBgColor,
    headerTextColor,
    bodyBgColor,
    bodyTextColor
  });

  return (
    <div
      className={
        `widget-container table-widget ${dynClass} ` +
        `${draggable ? 'is-draggable' : 'not-draggable'} ` +
        `${transparentBackground ? 'is-transparent' : ''}`
      }
      ref={widgetRef}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onMouseDown={(e) => setGrabbing(e, true)}
      onMouseUp={(e) => setGrabbing(e, false)}
      onMouseLeave={(e) => setGrabbing(e, false)}
    >
      {title && (
        <h3 className='table-title'>
          {title}
          {tableMetrics.titleSuffix}
        </h3>
      )}

      <div className="table-widget__grid">
        <div className="table-widget__topMeta">
          <span>Total Pages: {totalPages}</span>
        </div>
        <div
          className="table-widget__matrix"
          style={{ '--table-column-count': Math.max(cols.length, 1) }}
        >
          <div className="table-widget__row table-widget__row--header">
            {cols.map((column) => (
              <div className="table-widget__cell table-widget__cell--header" key={column.field}>
                <span>{column.headerName || column.field}</span>
                <span className="table-widget__chevron" aria-hidden="true" />
              </div>
            ))}
          </div>

          <div className="table-widget__body">
            {visibleRows.map((row) => (
              <div className="table-widget__row" key={row.id}>
                {cols.map((column) => (
                  <div className="table-widget__cell" key={column.field}>
                    {row.__empty ? '' : row[column.field] ?? 'Label Item'}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="table-widget__footer">
          <label className="table-widget__pageSize">
            <span>Rows per page:</span>
            <select value={paginationModel.pageSize} onChange={handlePageSizeChange}>
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <div className="table-widget__pager" aria-label="Table pagination">
            <button type="button" onClick={() => goToPage(0)} disabled={paginationModel.page === 0}>{"<<"}</button>
            <button type="button" onClick={() => goToPage(paginationModel.page - 1)} disabled={paginationModel.page === 0}>{"<"}</button>
            {visiblePageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                className={page === paginationModel.page ? 'is-active' : ''}
                onClick={() => goToPage(page)}
              >
                {page + 1}
              </button>
            ))}
            <button type="button" onClick={() => goToPage(paginationModel.page + 1)} disabled={paginationModel.page >= totalPages - 1}>{">"}</button>
            <button type="button" onClick={() => goToPage(totalPages - 1)} disabled={paginationModel.page >= totalPages - 1}>{">>"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

TableWidget.propTypes = {
  title: PropTypes.string,
  xData: PropTypes.arrayOf(PropTypes.string),
  curves: PropTypes.arrayOf(
    PropTypes.shape({
      pin: PropTypes.string.isRequired,
      deviceId: PropTypes.number.isRequired,
      yData: PropTypes.array.isRequired,
      yLabel: PropTypes.string,
      flex: PropTypes.number,
      customize: PropTypes.bool,
      min: PropTypes.number,
      max: PropTypes.number
    })
  ),

  draggable: PropTypes.bool,
  transparentBackground: PropTypes.bool,
  dims: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  headerBgColor: PropTypes.string,
  headerTextColor: PropTypes.string,
  bodyBgColor: PropTypes.string,
  bodyTextColor: PropTypes.string
};

export default TableWidget;
